import { Link } from "react-router-dom";
import { ArrowRight, Phone, Headphones, Users2, MapPin, Award, Linkedin } from "lucide-react";
import {
  FOUNDER_NAME,
  FOUNDER_TITLE,
  FOUNDER_LINKEDIN_URL,
} from "@/config/media";
import { useSiteAssetSingle } from "@/hooks/useSiteAssets";

const credentials = [
  { icon: Phone, label: "10+ years", sub: "Real estate cold calling operations" },
  { icon: Headphones, label: "8 years", sub: "Inside-sales & acquisition leadership" },
  { icon: Users2, label: "300+", sub: "Acquisition reps recruited & trained" },
  { icon: MapPin, label: "32 US markets", sub: "Active engagements across the country" },
];

const FounderAuthority = () => {
  const { asset } = useSiteAssetSingle("founder_photo");
  const photoUrl = asset?.file_url || null;
  return (
  <section className="section-padding bg-muted/40">
    <div className="container-narrow">
      <div className="grid items-start gap-12 lg:grid-cols-[auto_1fr]">
        <div className="mx-auto lg:mx-0">
          <div className="relative">
            <div className="absolute -inset-2 rounded-full bg-gradient-to-br from-secondary via-secondary/40 to-transparent blur-md" />
            <div className="relative rounded-full bg-secondary p-1.5">
              {photoUrl ? (
                <img
                  src={photoUrl}
                  alt={`${FOUNDER_NAME}, ${FOUNDER_TITLE}`}
                  loading="lazy"
                  className="h-40 w-40 rounded-full object-cover"
                />
              ) : (
                <div className="flex h-40 w-40 items-center justify-center rounded-full bg-primary font-display text-3xl font-extrabold text-primary-foreground">
                  EG
                </div>
              )}
            </div>
          </div>
          <div className="mt-4 flex flex-col items-center gap-2 lg:items-start">
            <div className="inline-flex items-center gap-1.5 rounded-full border border-secondary/40 bg-secondary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-foreground">
              <Award className="h-3 w-3 text-secondary" />
              {FOUNDER_TITLE}
            </div>
            <a
              href={FOUNDER_LINKEDIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1 text-[11px] font-semibold text-foreground hover:border-secondary hover:text-secondary"
              aria-label="Visit Elevate Global Solutions on LinkedIn"
            >
              <Linkedin className="h-3 w-3" />
              Connect on LinkedIn
            </a>
          </div>
        </div>

        <div>
          <div className="gold-divider" />
          <h2 className="mt-4 font-display text-3xl font-bold text-foreground md:text-4xl">
            Built by US real estate acquisition operators
          </h2>
          <p className="mt-4 text-muted-foreground">
            Elevate's leadership has spent the last decade inside US real estate acquisition, running
            cold calling rooms, building setter teams, coaching acquisition managers, and operating
            CRMs at scale across wholesale, cash buyer, and brokerage operations.
          </p>
          <p className="mt-3 text-muted-foreground">
            Every script, KPI, and SOP we deploy was built on the floor of a real US REI operation, 
            not borrowed from a generic BPO playbook. That's why our teams ramp in 10, 14 days and
            stay on-KPI through month three.
          </p>

          <div className="mt-7 grid gap-3 sm:grid-cols-2">
            {credentials.map((c) => (
              <div key={c.label} className="flex items-start gap-3 rounded-xl border border-border bg-background p-4">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary/15 text-secondary">
                  <c.icon className="h-4 w-4" />
                </div>
                <div>
                  <div className="font-display text-base font-extrabold text-foreground">{c.label}</div>
                  <div className="text-xs text-muted-foreground">{c.sub}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-4">
            <Link to="/about" className="inline-flex items-center text-sm font-semibold text-primary hover:text-secondary">
              Read the full leadership story
              <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
            <Link to="/book" className="inline-flex items-center rounded-md bg-secondary px-4 py-2 text-sm font-semibold text-secondary-foreground hover:bg-secondary/90">
              Talk to leadership
            </Link>
            <a
              href={FOUNDER_LINKEDIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-semibold text-foreground hover:border-secondary hover:text-secondary"
            >
              <Linkedin className="h-4 w-4" />
              LinkedIn
            </a>
          </div>
        </div>
      </div>
    </div>
    </section>
  );
};

export default FounderAuthority;
